import type { ReactNode } from 'react';
import { EMPLOYMENT_TYPE_LABELS, REGION_LABELS, TENDER_TYPE_LABELS } from './taxonomy';
import { KIND_LABELS } from './labels';
import { Deadline, formatShort } from './deadline';
import type { PostingListItem, Sort } from './types';

/**
 * The table's columns, declared ONCE.
 *
 * Header text, width class, sort wiring and cell renderer all live in the same object, so a header
 * cannot end up sitting over the wrong data. The alternative — a `<thead>` written out by hand and
 * a `<tbody>` written out by hand — desyncs the first time a column is inserted in one and not the
 * other, and produces a table that is wrong rather than broken: it renders, it just lies.
 *
 * READ RIGHT-TO-LEFT the row is chronological: published at the start, the posting itself in the
 * middle, the closing deadline at the end. That is the order a reader scans for "is this still
 * open", and it is why `published` leads rather than the title.
 */

export interface ColumnContext {
  onOpen: (item: PostingListItem) => void;
  /**
   * Frozen by the landing page's worked example; absent everywhere else, where `<Deadline>` reads
   * the real clock. Without it the example placed its sample deadlines against one `now` while the
   * cells rendered them against another, so the four urgency bands it exists to show silently
   * collapsed into three as the real calendar drifted past the frozen one.
   */
  now?: number;
}

export interface Column {
  key: string;
  label: string;
  /** Fixed or minimum width, so columns do not jump between pages of different content. */
  className?: string;
  /**
   * The sort this header applies, or `null` for a plain label.
   *
   * Only two columns sort. That was a deliberate scope decision, not an oversight: the others
   * would each need a Mongo index and an ETL-side sort key, and an index per column is a real cost
   * for an ordering nobody has asked for yet. A plain `<th>` says "not sortable" more honestly than
   * a button that silently does nothing.
   */
  sort: Sort | null;
  cell: (item: PostingListItem, ctx: ColumnContext) => ReactNode;
}

/** An absent value, marked as absent rather than left blank. */
const missing = <span className="tenders-table__muted">—</span>;

export const COLUMNS: Column[] = [
  {
    key: 'published',
    label: 'פורסם',
    className: 'tenders-table__c-published',
    /**
     * Sorted by the value it DISPLAYS. `newest` orders by the ETL's `sortPublished` — a non-null
     * mirror of `dates.published` — so this header and this column answer the same question.
     *
     * It briefly did not: the sort ran on `lifecycle.firstSeen`, when our own pipeline first saw the
     * posting. That agrees with the publication date for a daily crawl and separates on a backfill,
     * which is the worst kind of disagreement — invisible until the one day it matters.
     */
    sort: 'newest',
    cell: (item) => formatShort(item.dates.published) ?? missing,
  },

  {
    key: 'title',
    label: 'כותרת',
    className: 'tenders-table__c-title',
    sort: null,
    cell: (item, ctx) => (
      <>
        <button type="button" className="tenders-table__link" onClick={() => ctx.onOpen(item)}>
          {item.title}
        </button>
        {item.securityClearanceRequired === true && (
          <span className="badge badge-accent tenders-table__flag">סיווג</span>
        )}
      </>
    ),
  },

  {
    key: 'publisher',
    label: 'גוף מפרסם',
    className: 'tenders-table__c-publisher',
    sort: null,
    cell: (item) => item.source.publisher,
  },

  {
    key: 'city',
    label: 'עיר',
    className: 'tenders-table__c-city',
    sort: null,
    /**
     * ITS OWN COLUMN, not a ` · ` suffix on the publisher.
     *
     * The two are independent fields, and printed together they read as one duplicated value
     * wherever a municipality's name contains its city — "עיריית כפר סבא · כפר סבא" is the common
     * case, not the edge one. Split, an absent city is also stated as absent rather than silently
     * shortening the cell next door.
     *
     * FREE TEXT, not an enum — live data contains at least one English leak — so it is printed as
     * it arrives rather than looked up.
     */
    cell: (item) => item.city ?? missing,
  },

  {
    key: 'category',
    label: 'תחום',
    sort: null,
    // The server sends the label alongside the value; using it keeps one vocabulary on the wire.
    cell: (item) => item.categoryLabel ?? missing,
  },

  {
    key: 'region',
    label: 'אזור',
    sort: null,
    cell: (item) => (item.region ? REGION_LABELS[item.region] : missing),
  },

  {
    key: 'type',
    label: 'סוג',
    sort: null,
    cell: (item) => KIND_LABELS[item.kind],
  },

  {
    key: 'subtype',
    label: 'תת-סוג',
    sort: null,
    /**
     * ONE COLUMN OVER TWO FIELDS — the deliberate exception to the rule the file opens with.
     *
     * A tender carries `tenderType` and a job carries `employmentType`, never both: the ETL enforces
     * it when it builds the record, so the two are complementary rather than merely usually-absent.
     * Given a column each, every tender row would be `—` under היקף משרה and every job row `—` under
     * סוג מכרז — half the table dashes by construction, and two columns of width bought with it.
     *
     * The price is a header that names a role rather than a field, which is why it is `תת-סוג` and
     * not either field's own name — `סוג מכרז` sitting over a job's `משרה מלאה` is the lying header
     * this file exists to prevent. The `סוג` cell beside it says which field is being read.
     */
    cell: (item) => {
      const sub =
        item.kind === 'tender'
          ? item.tenderType && TENDER_TYPE_LABELS[item.tenderType]
          : item.employmentType && EMPLOYMENT_TYPE_LABELS[item.employmentType];
      return sub ?? missing;
    },
  },

  {
    key: 'due',
    label: 'מועד הגשה',
    className: 'tenders-table__c-due',
    sort: 'closing',
    // The same component the cards use, on one line. Sharing it is what keeps a posting's urgency
    // reading identically wherever it appears.
    cell: (item, ctx) => <Deadline iso={item.dates.lastSubmissionDate} variant="cell" now={ctx.now} />,
  },
];
